import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, DialogContent, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useImageUrl } from '@/hooks/useImageUrl';
import { imageUrlService } from '@/services/ImageUrlService';
import { debugLogger } from '@/utils/debugLogger';
import { motion, AnimatePresence } from 'framer-motion';

// Native image preload function (bypasses CORS issues that affect fetch())
const preloadImageNative = (url: string): Promise<void> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve(); // Resolve anyway to not block other preloads
    img.src = url;
  });
};

// Thumbnail component with async URL processing
const ThumbnailImage: React.FC<{ photo: string; index: number; className: string }> = ({ 
  photo, 
  index, 
  className 
}) => {
  const { url: thumbnailUrl } = useImageUrl(photo, 'thumbnail');
  return (
    <img 
      src={thumbnailUrl || photo} 
      alt={`Miniatura ${index + 1}`} 
      className={className}
    />
  );
};

interface ImageZoomProps {
  src: string;
  alt: string;
  children: React.ReactNode;
  conversationId?: string;
  mediaMessages?: Array<{
    id: string;
    media_url: string;
    created_at: string;
  }>;
  /** Simple array of photo URLs (alternative to mediaMessages for profiles) */
  photos?: string[];
  /** Show thumbnail bar at bottom */
  showThumbnails?: boolean;
  /** Initial index to start from */
  initialIndex?: number;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const ImageZoom: React.FC<ImageZoomProps> = ({ 
  src, 
  alt, 
  children, 
  conversationId,
  mediaMessages = [],
  photos = [],
  showThumbnails = false,
  initialIndex = 0,
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = controlledOnOpenChange || setInternalIsOpen;
  const [currentImageIndex, setCurrentImageIndex] = useState(initialIndex);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const isMobile = useIsMobile();
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const touchEndRef = useRef<{ x: number; y: number } | null>(null);
  const thumbnailsRef = useRef<HTMLDivElement>(null);
  
  // Ref para controlar se já inicializou nesta sessão do modal
  const hasInitializedRef = useRef(false);
  
  // Unified photo list (prioritize photos prop, fallback to mediaMessages)
  const allPhotos = useMemo(() => {
    return photos.length > 0 
      ? photos 
      : mediaMessages.map(m => m.media_url);
  }, [photos, mediaMessages]);
  
  // Sincronizar índice APENAS quando o modal ABRE (não durante navegação)
  useEffect(() => {
    // Reset flag quando modal fecha
    if (!isOpen) {
      hasInitializedRef.current = false;
      return;
    }
    
    // Se já inicializou para esta sessão, NÃO resetar
    if (hasInitializedRef.current) return;
    
    // Primeira vez abrindo - sincronizar com src
    if (allPhotos.length > 0) {
      const currentIndex = allPhotos.findIndex(photo => photo === src);
      if (currentIndex >= 0) {
        setCurrentImageIndex(currentIndex);
      } else if (initialIndex >= 0 && initialIndex < allPhotos.length) {
        setCurrentImageIndex(initialIndex);
      }
    } else if (mediaMessages.length > 0) {
      const currentIndex = mediaMessages.findIndex(msg => msg.media_url === src);
      if (currentIndex >= 0) {
        setCurrentImageIndex(currentIndex);
      } else {
        setCurrentImageIndex(0);
      }
    }
    
    // Marcar como inicializado para esta sessão
    hasInitializedRef.current = true;
  }, [isOpen, src, allPhotos, mediaMessages, initialIndex]);
  
  // Scroll thumbnail into view when index changes
  useEffect(() => {
    if (showThumbnails && thumbnailsRef.current) {
      const activeThumb = thumbnailsRef.current.querySelector(`[data-index="${currentImageIndex}"]`);
      activeThumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [currentImageIndex, showThumbnails]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const preventContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  const preventDragStart = (e: React.DragEvent) => {
    e.preventDefault();
    return false;
  };

  // Slide animation variants - simple slide without opacity
  const slideVariants = {
    enter: (direction: 'left' | 'right') => ({
      x: direction === 'left' ? '100%' : '-100%',
    }),
    center: {
      x: 0,
    },
    exit: (direction: 'left' | 'right') => ({
      x: direction === 'left' ? '-100%' : '100%',
    }),
  };

  // Navigation functions
  const goToPrevious = () => {
    if (allPhotos.length > 1) {
      setSlideDirection('right'); // Image enters from left
      const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : allPhotos.length - 1;
      setCurrentImageIndex(newIndex);
    }
  };

  const goToNext = () => {
    if (allPhotos.length > 1) {
      setSlideDirection('left'); // Image enters from right
      const newIndex = currentImageIndex < allPhotos.length - 1 ? currentImageIndex + 1 : 0;
      setCurrentImageIndex(newIndex);
    }
  };

  // Handle thumbnail click with direction detection
  const handleThumbnailClick = (index: number) => {
    if (index !== currentImageIndex) {
      setSlideDirection(index > currentImageIndex ? 'left' : 'right');
      setCurrentImageIndex(index);
    }
  };

  // Get current image URL
  const getCurrentMediaUrl = () => {
    if (allPhotos.length > 0 && allPhotos[currentImageIndex]) {
      return allPhotos[currentImageIndex];
    }
    return src;
  };

  // Use async image URL processing for high quality (zoom = variant 3 = 1200x1200)
  // This bypasses blob URL generation and returns direct signed URLs
  const { url: currentImageUrl, isLoading: isLoadingMainImage } = useImageUrl(
    getCurrentMediaUrl(),
    'zoom'
  );

  // Helper function to get adjacent image indices for preloading
  const getAdjacentIndices = (currentIndex: number, totalPhotos: number): number[] => {
    if (totalPhotos <= 1) return [];
    
    const indices: number[] = [];
    
    // Previous image (wrap around)
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : totalPhotos - 1;
    indices.push(prevIndex);
    
    // Next image (wrap around)
    const nextIndex = currentIndex < totalPhotos - 1 ? currentIndex + 1 : 0;
    if (nextIndex !== prevIndex) { // Avoid duplicate when only 2 photos
      indices.push(nextIndex);
    }
    
    return indices;
  };

  // Preload adjacent images for smooth navigation
  useEffect(() => {
    if (!isOpen || allPhotos.length <= 1) return;

    const preloadAdjacentImages = async () => {
      const adjacentIndices = getAdjacentIndices(currentImageIndex, allPhotos.length);
      
      debugLogger.debug('ImageZoom', 'Preloading adjacent images (native)', {
        currentIndex: currentImageIndex,
        adjacentIndices,
        totalPhotos: allPhotos.length
      });

      // Preload adjacent images using native Image() - bypasses CORS
      for (const index of adjacentIndices) {
        const photoUrl = allPhotos[index];
        if (!photoUrl) continue;
        
        try {
          const signedUrl = await imageUrlService.getImageUrlAsync(photoUrl, 'zoom');
          if (signedUrl) {
            await preloadImageNative(signedUrl);
            debugLogger.debug('ImageZoom', 'Preloaded image', { index });
          }
        } catch (error) {
          debugLogger.warn('ImageZoom', 'Failed to preload adjacent image', { index });
        }
      }
    };

    // Small delay to prioritize current image load first
    const timeoutId = setTimeout(preloadAdjacentImages, 300);
    
    return () => clearTimeout(timeoutId);
  }, [isOpen, currentImageIndex, allPhotos]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, currentImageIndex, allPhotos.length]);

  // Handle touch/swipe gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    
    touchEndRef.current = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY
    };

    const deltaX = touchStartRef.current.x - touchEndRef.current.x;
    const deltaY = Math.abs(touchStartRef.current.y - touchEndRef.current.y);
    
    // Minimum swipe distance and ensure horizontal swipe
    const minSwipeDistance = 50;
    const maxVerticalDistance = 100;
    
    if (Math.abs(deltaX) > minSwipeDistance && deltaY < maxVerticalDistance) {
      if (deltaX > 0) {
        // Swipe left - go to next image
        goToNext();
      } else {
        // Swipe right - go to previous image
        goToPrevious();
      }
    }
    
    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <div className="image-zoom-trigger">
          {children}
        </div>
      </DialogTrigger>
      <DialogContent 
        className={`max-w-[100vw] max-h-[100vh] w-full h-full bg-black/95 border-none p-0`}
      >
        <span className="sr-only" id="image-zoom-description">
          Visualização ampliada da imagem. Use as setas do teclado ou arraste para navegar entre imagens.
        </span>
        {/* Close button */}
        <DialogClose asChild>
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 z-50 w-12 h-12 bg-destructive hover:bg-destructive/90 text-white rounded-full"
          >
            <X className="h-6 w-6" />
          </Button>
        </DialogClose>

        {/* Navigation buttons */}
        {allPhotos.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevious}
              aria-label="Foto anterior"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-[60] w-12 h-12 bg-black/80 border border-white/50 hover:bg-black text-white rounded-full shadow-xl backdrop-blur-sm"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNext}
              aria-label="Próxima foto"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-[60] w-12 h-12 bg-black/80 border border-white/50 hover:bg-black text-white rounded-full shadow-xl backdrop-blur-sm"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </>
        )}

        {/* Image counter - positioned higher when thumbnails shown */}
        {allPhotos.length > 1 && (
          <div className={`absolute left-1/2 -translate-x-1/2 z-50 bg-black/50 text-white px-3 py-1 rounded-full text-sm ${showThumbnails ? 'bottom-28' : 'bottom-4'}`}>
            {currentImageIndex + 1} / {allPhotos.length}
          </div>
        )}
        
        {/* Thumbnails bar */}
        {showThumbnails && allPhotos.length > 1 && (
          <div className="absolute bottom-4 left-4 right-4 z-50">
            <div 
              ref={thumbnailsRef}
              className="flex gap-2 p-2 bg-black/60 backdrop-blur-md rounded-lg overflow-x-auto scrollbar-none"
            >
              {allPhotos.map((photo, index) => (
                <button
                  key={index}
                  data-index={index}
                  onClick={() => handleThumbnailClick(index)}
                  className={`flex-shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 transition-all ${
                    index === currentImageIndex
                      ? 'border-luxury-pinkBright scale-110 shadow-lg'
                      : 'border-white/30 opacity-70 hover:opacity-100 hover:border-white/50'
                  }`}
                >
                  <ThumbnailImage 
                    photo={photo} 
                    index={index}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Image container with slide animation */}
        <div 
          className="flex items-center justify-center w-full h-full overflow-hidden relative"
          style={{ touchAction: 'pan-x pan-y' }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Loading spinner while URL is being processed - prevents relative path fallback */}
          {isLoadingMainImage && (
            <div className="absolute inset-0 flex items-center justify-center z-40">
              <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          )}
          
          <AnimatePresence initial={false} custom={slideDirection}>
            {/* Only render image when we have a valid signed URL - never use relative path fallback */}
            {currentImageUrl && (
              <motion.img
                key={currentImageIndex}
                custom={slideDirection}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { duration: 0.25, ease: 'easeInOut' }
                }}
                src={currentImageUrl}
                alt={alt}
                className="w-full h-full object-contain select-none absolute"
                onContextMenu={preventContextMenu}
                draggable={false}
                style={{ 
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  maxWidth: '100vw',
                  maxHeight: '100vh'
                }}
              />
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};
